// The Work-home mark's stroke rendering: the glass-edge recipe, the per-piece
// stroke groups the entrance hook drives, and the crop's bottom fade. Split
// out of `WorkHomeMark.tsx` so that file stays a readable component; nothing
// here is imported outside it and its test.
import { m } from 'motion/react';
import type { JSX } from 'react';

import type { WorkHomeMarkTraceStroke } from './work-home-mark-motion';

/**
 * The GLASS EDGE recipe: the outline reads as the edge of a sheet of glass —
 * faint, see-through, refractive — with no fill.
 * Widths and offsets are SVG user units, so the edge thickens with the mark
 * (no `vectorEffect`); the three layers read as one refracted edge:
 *  - base: the outline itself, wider, accent yellow (the root's currentColor),
 *    faint at rest;
 *  - light: a thin hairline offset up-left in `text-text-primary-on-dark`,
 *    the token the shipped glass mark's glint uses — the lit edge of the
 *    glass, and what carries the outline's legibility at rest;
 *  - shade: a thin hairline offset down-right in `text-accent-amber-hover`,
 *    the darker refracted edge, very faint.
 * Crisp — no blur, no fill.
 */
export const TRACE_GLASS_EDGE = {
  // One recipe from the first drawn pixel to rest — the alphas are STATIC
  // attributes, so the stroke looks the same while drawing, rising and at rest.
  baseWidth: 1.0,
  baseOpacity: 0.25,
  lightWidth: 0.3,
  lightOffset: 0.35, // up-left, units
  lightToken: 'text-text-primary-on-dark',
  lightOpacity: 0.5,
  shadeWidth: 0.3,
  shadeOffset: 0.35, // down-right, units
  shadeToken: 'text-accent-amber-hover',
  shadeOpacity: 0.15,
} as const;

/**
 * The trace outline: per pen piece (see `work-home-mark-trace-path.ts`), ONE
 * `<m.g>` carrying the piece's animated `pathLength` — Motion writes the
 * dasharray/dashoffset on the group and the three child paths inherit them
 * (each child declares `pathLength={1}` so the dash is normalised to its own
 * length) — so a frame writes one dasharray per piece rather than three, and
 * the three strokes can never drift apart. Children, base under light under
 * shade, are plain `<path>`s with static widths/alphas/offsets.
 *
 * Joins: `strokeLinecap`/`strokeLinejoin` "round" on every path, so
 * two pieces meeting at a cusp (the notch tips, where their endpoints
 * coincide exactly) overlap in a round cap instead of leaving a butt-cap
 * wedge, and a closed loop's seam is a round join. The hairline offsets are
 * per whole piece, so offset neighbours still meet.
 */
export const traceStrokePaths = (
  strokes: readonly WorkHomeMarkTraceStroke[],
  glass: typeof TRACE_GLASS_EDGE
): JSX.Element[] =>
  strokes.map(({ piece, pathLength }) => (
    <m.g
      key={piece.id}
      data-trace-piece={piece.id}
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ pathLength }}
    >
      <path
        data-trace-layer="base"
        d={piece.d}
        fill="none"
        pathLength={1}
        strokeWidth={glass.baseWidth}
        strokeOpacity={glass.baseOpacity}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        data-trace-layer="light"
        className={glass.lightToken}
        d={piece.d}
        fill="none"
        pathLength={1}
        strokeWidth={glass.lightWidth}
        strokeOpacity={glass.lightOpacity}
        strokeLinecap="round"
        strokeLinejoin="round"
        transform={`translate(${-glass.lightOffset} ${-glass.lightOffset})`}
      />
      <path
        data-trace-layer="shade"
        className={glass.shadeToken}
        d={piece.d}
        fill="none"
        pathLength={1}
        strokeWidth={glass.shadeWidth}
        strokeOpacity={glass.shadeOpacity}
        strokeLinecap="round"
        strokeLinejoin="round"
        transform={`translate(${glass.shadeOffset} ${glass.shadeOffset})`}
      />
    </m.g>
  ));

/**
 * The crop's bottom fade: a white-to-transparent gradient masking the crop
 * box (`-2 -2 52 <heightUnits>`) so the outline melts into the composer —
 * the same fade this repo shipped for its earlier half-logo crop (git history
 * around f84aa2ef/40fe71b5), sized to the crop height in use.
 */
export const halfCropDefs = (id: string, heightUnits: number): JSX.Element => (
  <>
    <linearGradient id={`${id}-fade`} x1="0" y1="0" x2="0" y2="1">
      <stop stopColor="white" />
      <stop offset="0.4" stopColor="white" stopOpacity="0.7" />
      <stop offset="1" stopColor="white" stopOpacity="0" />
    </linearGradient>
    <mask
      id={`${id}-mask`}
      maskUnits="userSpaceOnUse"
      x="-2"
      y="-2"
      width="52"
      height={heightUnits}
    >
      <rect x="-2" y="-2" width="52" height={heightUnits} fill={`url(#${id}-fade)`} />
    </mask>
  </>
);
